import type {
  AssessmentRecordItem,
  ImplementationObjectRef,
  ImplementationRecord,
} from "../../../services/implementationRecordService";

/** Title-case each word; `_` and `-` become spaces. */
export function formatAssessmentSourceLabel(source: string): string {
  const trimmed = source.trim();
  if (!trimmed) return "-";
  return trimmed
    .split(/[-_]+/)
    .filter(Boolean)
    .map((part) => {
      const lower = part.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
}

export function toAssessmentSourceFilterOptions(
  options: readonly { value: string; label: string }[],
): { value: string; label: string }[] {
  return options.map((option) => ({
    value: option.value,
    label:
      option.label && option.label !== option.value
        ? option.label
        : formatAssessmentSourceLabel(option.value),
  }));
}

export function formatImpactYn(impacted: boolean): "Y" | "N" {
  return impacted ? "Y" : "N";
}

export function formatImpactedLevelLabel(level: string | null | undefined): string {
  const key = level?.trim().toLowerCase() ?? "";
  if (!key) return "";
  if (key === "no") return "No";
  return key.charAt(0).toUpperCase() + key.slice(1);
}

/**
 * Executed Assessments grid: each concept value is `display_name. name`.
 * Both arrays are flattened and joined with ", ".
 */
export function formatAssessmentRecordItems(
  items: readonly AssessmentRecordItem[] | null | undefined,
): string {
  if (!items?.length) return "";
  const parts: string[] = [];
  for (const item of items) {
    const displayName = item.displayName.trim();
    if (item.values.length === 0) {
      if (displayName) parts.push(displayName);
      continue;
    }
    for (const value of item.values) {
      const name = value.name.trim();
      if (displayName && name) {
        parts.push(`${displayName}. ${name}`);
      } else if (name || displayName) {
        parts.push(name || displayName);
      }
    }
  }
  return parts.join(", ");
}

/** Executed Assessments grid: Impact N has no level, so the cell is NA. */
export function formatImpactLevelCell(
  impacted: boolean,
  level: string | null | undefined,
): string {
  if (!impacted) return "NA";
  return displayOrDash(formatImpactedLevelLabel(level));
}

/** Chip copy such as "High impact". Empty when the level is missing. */
export function formatImpactLevelChip(level: string | null | undefined): string {
  const label = formatImpactedLevelLabel(level);
  if (!label) return "";
  return `${label} impact`;
}

function joinIdAndVersion(id: string, version: string | null | undefined): string {
  const trimmedId = id.trim();
  const trimmedVersion = version?.trim() ?? "";
  if (trimmedId && trimmedVersion) return `${trimmedId} - ${trimmedVersion}`;
  return trimmedId || trimmedVersion;
}

/**
 * Impacted Items & ID.
 * `concept.*` (any case): `object_type_name (name)`.
 * Otherwise: `object_type_name (id - version)`. Version is omitted when empty.
 */
export function formatImpactedItemsLabel(object: ImplementationObjectRef): string {
  const typeName = object.objectTypeName.trim();
  const isConcept = object.objectType.trim().toLowerCase().startsWith("concept.");
  const detail = isConcept
    ? object.name.trim()
    : joinIdAndVersion(object.id, object.version);
  if (typeName && detail) return `${typeName} (${detail})`;
  return typeName || detail || "-";
}

/** Reference Items & ID: `object_type_name (id - version)`. Version is omitted when empty. */
export function formatReferenceItemsLabel(object: ImplementationObjectRef): string {
  const type = object.objectTypeName.trim();
  const idWithVersion = joinIdAndVersion(object.id, object.version);
  if (type && idWithVersion) return `${type} (${idWithVersion})`;
  return type || idWithVersion || "-";
}

export function displayOrDash(value: string | null | undefined): string {
  const trimmed = value?.trim() ?? "";
  return trimmed || "-";
}

export function formatObjectItemLines(object: ImplementationObjectRef): {
  title: string;
  detail: string;
} {
  const title = object.name.trim() || object.id.trim() || "-";
  const id = object.id.trim();
  const version = object.version?.trim() ?? "";
  const detail = [id, version].filter(Boolean).join(" · ");
  return { title, detail };
}

export function assessorInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
}

export function impactedObjectKey(object: ImplementationObjectRef): string {
  return [
    object.objectType,
    object.objectSubType,
    object.id,
    object.version ?? "",
  ].join("::");
}

export type PackTreeChild = {
  key: string;
  label: string;
  object: ImplementationObjectRef;
  records: ImplementationRecord[];
};

export type PackTreeGroup = {
  assessmentName: string;
  assessmentSource: string;
  recordCount: number;
  children: PackTreeChild[];
};

export function buildPackTree(
  groups: { assessmentName: string; assessmentSource: string; recordCount: number }[],
  records: ImplementationRecord[],
): PackTreeGroup[] {
  return groups.map((group) => {
    const groupRecords = records.filter(
      (record) => record.assessmentName === group.assessmentName,
    );
    const childMap = new Map<string, PackTreeChild>();
    for (const record of groupRecords) {
      const key = impactedObjectKey(record.impactedObject);
      const existing = childMap.get(key);
      if (existing) {
        existing.records.push(record);
        continue;
      }
      const lines = formatObjectItemLines(record.impactedObject);
      childMap.set(key, {
        key,
        label: lines.title,
        object: record.impactedObject,
        records: [record],
      });
    }
    return {
      assessmentName: group.assessmentName,
      assessmentSource: group.assessmentSource,
      recordCount: group.recordCount,
      children: [...childMap.values()],
    };
  });
}
